import {
  CART_TYPE,
  CONSUMER_CMS_SHOPPING_CART,
  getCostFromShoppingCartByType,
  IGenericErrorCMS,
  IProduct,
  retrieveProduct,
  useAddMobileAddon,
  useCmsConfig,
  useGetShoppingCartSilentUpdate,
  useRemoveMobileAddon,
} from '@vfit/consumer/data-access';
import { API, getFromLocalStorageByKey, LoggerInstance } from '@vfit/shared/data-access';
import { useEffect } from 'react';
import { IUpdateProductPriceType } from '@vfit/shared/models';
import { useQueryClient } from 'react-query';
import {
  getReferenceId,
  getReferenceIdFromQueryClient,
  removeAddonFromQueryClient,
  saveAddonOnQueryClient,
} from './useAddonFlowMobile.utils';
import { useCheckout } from '../../../iBuyMobile.context';
import { DEFAULT_ADDON_STATE } from '../../../iBuyMobile.context.data';
import { updateProductPrice } from '../../../ShoppingCart/shoppingCart.utils';

export const useAddonFlowMobile = () => {
  const { addons, setAddons, addonState, setAddonState, product, setProduct, checkoutErrors } =
    useCheckout();

  const { addonCmsId, addonMecId, availableAddons, callSaveAddon, callRemoveAddon } = addonState;
  const shoppingCart = getFromLocalStorageByKey('shoppingCart');
  const cartId: string = shoppingCart?.id || '';
  const t_prod = retrieveProduct();

  const queryClient = useQueryClient();

  const genericError = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_GENERIC_ERROR,
  ) as IGenericErrorCMS;

  const {
    mutate: saveAddonMutation,
    data: saveAddonData,
    isSuccess: isSuccessAddition,
    isError: isErrorAddition,
    isLoading: isLoadingAddition,
  } = useAddMobileAddon();

  const {
    mutate: removeAddonMutation,
    data: removeAddonData,
    isSuccess: isSuccessRemoval,
    isError: isErrorRemoval,
    isLoading: isLoadingRemoval,
  } = useRemoveMobileAddon();

  const {
    isSuccess: isSuccessShoppingCart,
    data: shoppingCartData,
    refetch: refetchShoppingCart,
    isFetching: isLoadingShoppingCart,
    isError: isErrorShoppingCart,
  } = useGetShoppingCartSilentUpdate(cartId, {
    enabled: false,
    keyDependency: ['getShoppingCartAddons'],
  });

  const saveAddon = () => {
    if (addonCmsId && addonMecId) {
      saveAddonMutation({ cartId, addonId: addonMecId });
    } else {
      LoggerInstance.error('Error in offer update');
      setAddonState({ ...DEFAULT_ADDON_STATE, availableAddons });
    }
  };

  const removeAddon = () => {
    if (addonCmsId && addonMecId) {
      const referenceId = getReferenceIdFromQueryClient(queryClient, addonMecId);
      removeAddonMutation({ cartId, addonId: referenceId });
    } else {
      LoggerInstance.error('Error in offer update');
      setAddonState({ ...DEFAULT_ADDON_STATE, availableAddons });
    }
  };

  useEffect(() => {
    if (callSaveAddon) {
      saveAddon();
    }
    if (callRemoveAddon) {
      removeAddon();
    }
  }, [callSaveAddon, callRemoveAddon]);

  useEffect(() => {
    if (isSuccessAddition) {
      const referenceId = getReferenceId(addonMecId, saveAddonData);

      saveAddonOnQueryClient(queryClient, addonMecId, referenceId);
      setAddons([...addons, addonCmsId]);
      setAddonState({ ...DEFAULT_ADDON_STATE, availableAddons });
      refetchShoppingCart();
    }
  }, [isSuccessAddition, saveAddonData]);

  useEffect(() => {
    if (isSuccessRemoval) {
      setAddons(addons.filter((addn) => addn !== addonCmsId));
      removeAddonFromQueryClient(queryClient, addonMecId);
      setAddonState({ ...DEFAULT_ADDON_STATE, availableAddons });
      refetchShoppingCart();
    }
  }, [isSuccessRemoval, removeAddonData]);

  useEffect(() => {
    const addonJson = JSON.stringify(addons);
    localStorage.setItem('addons', addonJson);
  }, [addons]);

  useEffect(() => {
    if (isErrorAddition || isErrorRemoval || isErrorShoppingCart) {
      setAddonState({ ...DEFAULT_ADDON_STATE, availableAddons });
      checkoutErrors?.showAndTrackError?.(genericError, () => {
        return;
      });
    }
  }, [isErrorAddition, isErrorRemoval, isErrorShoppingCart]);

  useEffect(() => {
    if (isSuccessShoppingCart && shoppingCartData) {
      const recurringAmountCost = getCostFromShoppingCartByType(CART_TYPE.RECURRING);
      // only for addons
      const updatedProduct: IProduct = {
        ...t_prod,
        price: recurringAmountCost?.formattedPrice?.toString(),
      };
      localStorage.setItem('tProd', JSON.stringify(updatedProduct));
      setProduct({
        ...product,
        ...updateProductPrice(product, IUpdateProductPriceType.EDIT),
        price: recurringAmountCost?.formattedPrice?.toString(),
      });
    }
    setAddonState({ ...DEFAULT_ADDON_STATE, availableAddons });
  }, [isSuccessShoppingCart, shoppingCartData]);

  useEffect(() => {
    if (isLoadingAddition || isLoadingRemoval) {
      setAddonState({
        ...addonState,
        isLoadingUpdate: true,
      });
    }
  }, [isLoadingAddition, isLoadingRemoval]);

  useEffect(() => {
    if (isLoadingShoppingCart) {
      setAddonState({
        ...addonState,
        isLoadingPriceUpdate: true,
      });
    } else {
      setAddonState({ ...DEFAULT_ADDON_STATE, availableAddons });
    }
  }, [isLoadingShoppingCart]);

  return null;
};

